import { Directive, ElementRef, OnInit, inject } from "@angular/core";
import { Config } from "@ionic/angular";

/**
 * Keeps the <ion-select> FIELD in the Android style (mode: "md") to match the
 * app theme, while its interface overlay (the alert/popover/action-sheet with the
 * options) follows the platform-native mode (ios on iOS, md on Android) — so the
 * radio/checkbox options look native on iOS instead of Android-style.
 *
 * Ionic builds the overlay as `Object.assign({ mode }, interfaceOptions)` (see
 * ion-select openAlert/openPopover/openActionSheet), so `interfaceOptions.mode`
 * overrides the select's own mode for the overlay ONLY. That lets us split the
 * field mode from the popup mode.
 *
 * - The field mode is set in the constructor (before the web component's
 *   connectedCallback reads it) and only when no explicit `mode` is present, so a
 *   select can still opt out with mode="ios".
 * - The overlay mode is merged into interfaceOptions in ngOnInit, after any
 *   template [interfaceOptions] binding has been applied, so existing options are
 *   preserved.
 *
 * It acts on the <ion-select> of the components that list it in their
 * `imports`: a component that shows an <ion-select> imports it.
 */
@Directive({ selector: "ion-select" })
export class MdSelectDirective implements OnInit {
  private readonly el = inject<ElementRef<HTMLIonSelectElement>>(ElementRef);
  private readonly config = inject(Config);

  constructor() {
    if (!this.el.nativeElement.hasAttribute("mode")) {
      this.el.nativeElement.setAttribute("mode", "md");
    }
  }

  ngOnInit(): void {
    const platformMode = this.config.get("mode");
    if (platformMode) {
      const select = this.el.nativeElement;
      select.interfaceOptions = {
        ...(select.interfaceOptions ?? {}),
        mode: platformMode,
      };
    }
  }
}
